/**
 * Infinite loading for the collection / search product grid.
 *
 * Progressive enhancement over the existing pagination rather than a
 * replacement for it:
 *
 *  - The server still renders {% render 'pagination' %}. With no JS that is
 *    the whole feature, unchanged, and it stays crawlable - search engines
 *    follow the real <a href> to page 2, which an intersection-triggered
 *    fetch alone would never give them.
 *  - It is also the source of truth for "what is next". This file never
 *    builds a ?page=N URL itself; it reads paginate.next's href out of the
 *    live #Pagination markup, so filters, sort and query params come along
 *    for free (Shopify already writes them into that href) and there is no
 *    second place that has to know how the URL is composed.
 *
 * Each load fetches the next page, appends the new cards to the live grid,
 * and swaps #Pagination for the newly rendered one - which is what advances
 * "next" to the page after it. When there is no next link left, the observer
 * disconnects.
 *
 * A real <button> is rendered too, not just a sentinel: auto-loading on
 * scroll leaves keyboard and screen-reader users with no way to reach page
 * two, and it is the honest fallback if IntersectionObserver never fires
 * (a short viewport, a zoomed-in page). The observer simply clicks it.
 */
(function () {
  if (!('IntersectionObserver' in window)) return;

  var LOAD_MORE_ROOT_MARGIN = '600px';

  function InfiniteScroll(root) {
    this.root = root;
    this.loading = false;
    this.grid = null;
    this.button = null;
    this.observer = null;

    this.onClick = this.onClick.bind(this);
    this.setup();
  }

  InfiniteScroll.prototype.setup = function () {
    this.grid = document.getElementById('ProductGrid');
    if (!this.grid) return;

    /* The numbered links are redundant once this is running, but they stay in
       the DOM: they are where nextUrl() reads from, and hiding them is a
       presentation concern, so CSS owns it via this class on <html>. Set
       before the next-page check, so a grid that has no next page still
       hides them. */
    document.documentElement.classList.add('has-infinite-scroll');

    var nextUrl = this.nextUrl();
    if (!nextUrl) {
      this.teardownButton();
      return;
    }

    if (!this.button) {
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'btn btn--secondary infinite-scroll__button';
      this.button.textContent = this.root.dataset.loadMoreLabel || 'Load more';
      this.button.addEventListener('click', this.onClick);

      this.wrapper = document.createElement('div');
      this.wrapper.className = 'infinite-scroll';
      this.wrapper.appendChild(this.button);

      var pagination = document.getElementById('Pagination');
      if (pagination && pagination.parentElement) {
        pagination.parentElement.insertBefore(this.wrapper, pagination);
      } else {
        this.grid.parentElement.appendChild(this.wrapper);
      }
    }

    if (!this.observer) {
      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) self.loadNext();
        },
        { rootMargin: LOAD_MORE_ROOT_MARGIN }
      );
    }
    this.observer.observe(this.button);
  };

  InfiniteScroll.prototype.teardownButton = function () {
    if (this.observer) this.observer.disconnect();
    if (this.wrapper && this.wrapper.parentElement) this.wrapper.parentElement.removeChild(this.wrapper);
    this.wrapper = null;
    this.button = null;
  };

  InfiniteScroll.prototype.nextUrl = function () {
    var pagination = document.getElementById('Pagination');
    if (!pagination) return null;
    var next = pagination.querySelector('.pagination__item--next a[href]');
    return next ? next.getAttribute('href') : null;
  };

  InfiniteScroll.prototype.onClick = function () {
    this.loadNext();
  };

  InfiniteScroll.prototype.loadNext = function () {
    if (this.loading) return;
    var url = this.nextUrl();
    if (!url) {
      this.teardownButton();
      return;
    }

    this.loading = true;
    this.button.classList.add('is-loading');
    this.button.disabled = true;

    var self = this;
    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('page fetch failed');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.getElementById('ProductGrid');
        var liveGrid = document.getElementById('ProductGrid');
        if (!newGrid || !liveGrid) throw new Error('grid missing');

        var added = [];
        Array.prototype.slice.call(newGrid.children).forEach(function (card) {
          liveGrid.appendChild(card);
          added.push(card);
        });

        /* Swapping in the freshly rendered pagination is what advances
           "next" - without it nextUrl() would keep returning the same page
           and every load would append the same cards. */
        var newPagination = doc.getElementById('Pagination');
        var livePagination = document.getElementById('Pagination');
        if (newPagination && livePagination) {
          livePagination.replaceWith(newPagination);
        } else if (livePagination) {
          livePagination.remove();
        }

        /* These cards arrived by fetch, so neither DOMContentLoaded nor
           shopify:section:load ever fires for them - and those are the only
           two things that start reveal.js. Without this they keep their
           [data-media-skeleton] shimmer forever, which reads as a grid stuck
           loading even though every image has arrived. */
        if (window.ThemeReveal) window.ThemeReveal.init(liveGrid);

        /* The address bar is deliberately NOT updated to ?page=N. It used to
           be, so a refresh loaded page N on its own - the earlier products
           gone - and on the last page the numbered pagination showed instead
           of infinite loading. The grid is one continuous list, so its URL is
           page one. */

        self.loading = false;
        if (self.button) {
          self.button.classList.remove('is-loading');
          self.button.disabled = false;
        }

        if (!self.nextUrl()) {
          self.teardownButton();
        } else if (self.observer && self.button) {
          /* Re-observe: the button element is unchanged, but the pagination
             it sat beside was replaced, so make sure it is still watched. */
          self.observer.observe(self.button);
        }
      })
      .catch(function () {
        /* Leave the numbered pagination in place and working - a failed
           fetch must not strand the visitor with no way forward. */
        self.loading = false;
        document.documentElement.classList.remove('has-infinite-scroll');
        if (self.button) {
          self.button.classList.remove('is-loading');
          self.button.disabled = false;
        }
      });
  };

  var instance = null;

  /* A ?page=N address on an infinite grid - a refresh from before this
     stopped writing it, or a shared link - would render page N on its own.
     Start the list from the top instead. Crawlers and no-JS visitors never
     run this, so the numbered pages stay reachable for them. */
  function restartFromFirstPage() {
    var params = new URLSearchParams(window.location.search);
    var page = parseInt(params.get('page'), 10);
    if (!(page > 1)) return false;
    params.delete('page');
    var query = params.toString();
    window.location.replace(window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    return true;
  }

  function init(event) {
    var root = document.querySelector('[data-infinite-scroll]');
    if (!root) {
      document.documentElement.classList.remove('has-infinite-scroll');
      return;
    }
    /* Only on a real page load - not on a filter change or an editor
       re-render, which pass their event here. */
    if (!event && restartFromFirstPage()) return;
    if (instance) {
      instance.teardownButton();
      instance.observer = null;
    }
    instance = new InfiniteScroll(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* facets.js replaces #ProductGrid and #Pagination wholesale on every
     filter/sort change, which detaches the button and leaves the observer
     watching a node that is no longer in the document. Rebuild from
     whatever is on screen now. */
  document.addEventListener('facets:updated', init);
  document.addEventListener('shopify:section:load', init);
})();
